import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LoginService } from 'login';
import { firstValueFrom } from 'rxjs';
import { ENV } from 'src/providers/environment.provider';
import { SKIP_INTERCEPTOR } from 'src/types/custom-headers';
import { Environment } from 'src/types/environment';
import { LsApplicationTenant } from 'src/types/ls-application-tenant';
import { LsApplicationTenantsResponse } from 'src/types/ls-application-tenants-response';
import { LsLicense } from 'src/types/ls-license';
import { LsLicenseClientConfig as LsLicenseTenantConfig } from 'src/types/ls-license-client-config';

const LICENSE_EXPIRE_DURATION = 10000;

interface CognitoSsoConfig {
  tenantId: string;
  tenantName: string;
  sso: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private license?: Promise<LsLicense>;
  private tenant?: Promise<CognitoSsoConfig>;

  constructor(
    @Inject(ENV) private readonly env: Environment,
    private readonly http: HttpClient,
    private readonly loginService: LoginService
  ) { }

  /**
   * Gets all the application tenants.
   * @returns All the application tenants.
   */
  public async getAll(): Promise<LsApplicationTenant[]> {
    const authHeaders = await this.loginService.getHttpAuthorisationHeaders();
    const url = this.env.licenseServerUrl + 'ApplicationTenants';
    const headers = authHeaders.append(SKIP_INTERCEPTOR, 'true');
    const response = await firstValueFrom(this.http.post<LsApplicationTenantsResponse>(url, {
      ApplicationShortName: this.env.appShortName
    }, {
      headers: headers
    }));
    return response.tenants;
  }

  public async getAuthorizationHeaders(): Promise<HttpHeaders> {
    const license = await this.getLicense();
    const authHeaders = await this.loginService.getHttpAuthorisationHeaders();
    const headers = authHeaders.append('License', license.token);
    return headers;
  }

  public async getApiUrl(): Promise<string> {
    if (this.env.configuration === 'local') { return 'http://localhost:3000/' }
    else {
      const license = await this.getLicense();
      const tenantConfig: LsLicenseTenantConfig = JSON.parse(license.tenantConfig);
      return `https://${tenantConfig.apiid}.execute-api.${tenantConfig.region}.amazonaws.com/${tenantConfig.stage}/`;
    }
  }

  public async getTenant(): Promise<CognitoSsoConfig> {
    this.tenant ??= (async () => {
      const email = await this.loginService.getUserEmail();
      const headers = (await this.loginService.getHttpAuthorisationHeaders()).append(SKIP_INTERCEPTOR, 'true');
      const config = await firstValueFrom(this.http.get<CognitoSsoConfig>(
        this.env.licenseServerUrl + `TenantApplications?email=${email}&appName=${this.env.appShortName}`,
        { headers: headers }));
      return config;
    })();

    return await this.tenant;
  }

  private async getLicense(): Promise<LsLicense> {
    if (!this.license || this.getIsLicenseExpired(await this.license)) {
      this.license = (async () => {
        const url = this.env.licenseServerUrl + 'license';
        const email = await this.loginService.getUserEmail();
        const authHeaders = await this.loginService.getHttpAuthorisationHeaders();
        const headers = authHeaders.append(SKIP_INTERCEPTOR, 'true');
        const body = { email: email, appName: this.env.appShortName };
        return await firstValueFrom(this.http.post<LsLicense>(url, body, { headers: headers }));
      })();
    }

    return this.license;
  }

  private getIsLicenseExpired(license?: LsLicense): boolean {
    if (!license) { return true; }
    const now = new Date();
    const expiryDate = new Date(license.expiryUtc);
    const remainingMilliseconds = expiryDate.getTime() - now.getTime();
    return remainingMilliseconds < LICENSE_EXPIRE_DURATION;
  }
}